import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import moment from "moment-timezone";
import "../assets/css/RetreatStatusModal.css";

const EDITOR_OPTIONS = {
  height: 280,
  buttonList: [
    ["undo", "redo"],
    ["bold", "underline", "italic", "removeFormat"],
    ["fontColor", "hiliteColor"],
    ["align", "list", "link"],
    ["fullScreen", "codeView"],
  ],
};

const capitalize = (value) =>
  value ? `${value[0].toUpperCase()}${value.slice(1)}` : "";

const formatRange = (from, to) => {
  const start = from ? moment(from) : null;
  const end = to ? moment(to) : null;
  if (start?.isValid() && end?.isValid()) {
    return `${start.format("MM-DD-YYYY")} – ${end.format("MM-DD-YYYY")}`;
  }
  if (start?.isValid()) return start.format("MM-DD-YYYY");
  return "";
};

function buildDefaultMessage(alertData) {
  const parish = alertData?.parishName
    ? `${alertData.parishName[0].toUpperCase()}${alertData.parishName.slice(1)}`
    : "";
  const language = alertData?.language
    ? `${alertData.language[0].toUpperCase()}${alertData.language.slice(1)}`
    : "";

  if (alertData?.language === "spanish") {
    return `<div class="message-container">
  <h4 style="line-height:27px;">Gracias por su interés en los <strong>Retiros Matrimoniales LOVESTRONG</strong>. 
  Lamentamos informarle que este Retiro LOVESTRONG ha alcanzado su capacidad máxima y ya no está aceptando inscripciones.</h4>

  <h4 style="line-height:27px;">Estamos planeando realizar otro Retiro LOVESTRONG en su parroquia en un futuro cercano. 
  Por favor contáctenos en <span><a href="mailto:info@LOVESTRONGMarriage.com">info@LOVESTRONGMarriage.com</a></span> 
  y envíenos sus nombres y direcciones de correo electrónico. Lo colocaremos en una <strong>Lista de Contacto Prioritaria</strong>, 
  para que podamos comunicarnos con usted con las fechas e información una vez que el próximo 
  <strong>${parish} 
  (${language})</strong> 
  Retiro LOVESTRONG esté programado para el otoño de 2025.</h4>

  <h4 style="line-height:27px;">También puede visitar 
  <a href="https://LOVESTRONGMarriage.com" target="_blank">LOVESTRONGMarriage.com</a> 
  para obtener actualizaciones sobre futuros Retiros LOVESTRONG y eventos especiales.</h4>

  <h4 style="line-height:27px;">Gracias y que Dios los bendiga.</h4>
  <h4 style="line-height:27px;"><strong>- El equipo de LOVESTRONG</strong></h4>
</div>`;
  }

  return `
    <div class="message-container">
      <h4 style="line-height:27px;">Thanks for your interest in <strong>LOVESTRONG Marriage Retreats</strong>. 
      We are so sorry, but this LOVESTRONG Retreat has exceeded capacity and is no longer accepting registrations.</h4>

      <h4 style="line-height:27px;">We are planning on hosting another LOVESTRONG Retreat at your parish in the near future. 
      Please contact us at <span><a href="mailto:info@LOVESTRONGMarriage.com">info@LOVESTRONGMarriage.com</a></span> 
      and send us your names and email address(es). You will be placed on a <strong>Priority Contact List</strong>, 
      so that we can reach out to you with dates and information once the next 
      <strong>${parish} 
      (${language})</strong> 
      LOVESTRONG Retreat has been set for next Fall 2025.</h4>

      <h4 style="line-height:27px;">You can also visit 
      <a href="https://LOVESTRONGMarriage.com" target="_blank">LOVESTRONGMarriage.com</a> 
      to get updates on other future LOVESTRONG Retreats and special events.</h4>

      <h4 style="line-height:27px;">Thank you and God bless.</h4>
      <h4 style="line-height:27px;"><strong>- LOVESTRONG Staff</strong></h4>
    </div>
  `;
}

function RetreatStatusModal({
  show,
  onHide,
  alertData = {},
  editBox = false,
  onConfirm,
}) {
  const isActive = alertData?.status === "ACTIVE";
  const showReasonBox = isActive && !editBox;
  const needsEditor = showReasonBox || editBox;
  const [reason, setReason] = useState("");
  const [editorSeed, setEditorSeed] = useState("");
  const [error, setError] = useState("");

  const defaultMessage = useMemo(
    () => buildDefaultMessage(alertData),
    [alertData],
  );

  useEffect(() => {
    if (!show) {
      setReason("");
      setEditorSeed("");
      setError("");
      return;
    }
    if (!needsEditor) return;
    const next =
      alertData?.inactiveReason && alertData.inactiveReason.trim() !== ""
        ? alertData.inactiveReason
        : defaultMessage;
    setReason(next);
    setEditorSeed(next);
  }, [show, needsEditor, alertData, defaultMessage]);

  const retreatName = [
    capitalize(alertData?.parishName) || "This retreat",
    alertData?.language ? `(${capitalize(alertData.language)})` : "",
    capitalize(alertData?.location),
  ]
    .filter(Boolean)
    .join(" ");

  const dateLabel = formatRange(alertData?.dateFrom, alertData?.dateTo);

  const title = editBox
    ? "Edit inactive message"
    : isActive
      ? "Deactivate retreat"
      : "Activate retreat";

  const subtitle = editBox
    ? "Update the note couples see while registration is closed."
    : isActive
      ? "Registration stops until you turn this retreat back on."
      : "Couples will be able to register for this retreat again.";

  const confirmLabel = editBox
    ? "Save message"
    : isActive
      ? "Deactivate"
      : "Activate";

  const close = () => {
    setReason("");
    setError("");
    onHide();
  };

  const handleConfirm = () => {
    if (showReasonBox && (!reason || reason.trim() === "")) {
      setError("Add a message before deactivating this retreat.");
      return;
    }
    setError("");
    onConfirm(reason);
  };

  return (
    <Modal
      show={show}
      onHide={close}
      centered
      backdrop="static"
      className="rs-modal"
      dialogClassName={`rs-dialog${needsEditor ? " rs-dialog-wide" : ""}`}
      contentClassName="rs-content"
    >
      <div className="rs-shell">
        <header className="rs-header">
          <div className={`rs-mark ${isActive && !editBox ? "is-off" : "is-on"}`}>
            <i className={`mdi ${isActive && !editBox ? "mdi-pause" : "mdi-play"}`} />
          </div>
          <div className="rs-header-copy">
            <p className="rs-kicker">Retreat status</p>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
          <button type="button" className="rs-close" onClick={close} aria-label="Close">
            <i className="mdi mdi-close" />
          </button>
        </header>

        <div className="rs-body">
          <section className="rs-summary">
            <div>
              <span>Retreat</span>
              <strong>{retreatName}</strong>
              {dateLabel ? <small>{dateLabel}</small> : null}
            </div>
            {editBox ? (
              <div className="rs-flow">
                <em className="is-off">Inactive</em>
              </div>
            ) : (
              <div className="rs-flow" aria-label="Status change">
                <em className={isActive ? "is-on" : "is-off"}>
                  {isActive ? "Active" : "Inactive"}
                </em>
                <i className="mdi mdi-arrow-right" aria-hidden="true" />
                <em className={isActive ? "is-off" : "is-on"}>
                  {isActive ? "Inactive" : "Active"}
                </em>
              </div>
            )}
          </section>

          {needsEditor ? (
            <section className="rs-editor">
              <div className="rs-editor-head">
                <h3>Message couples will see</h3>
                <p>
                  Shown when someone opens this retreat while registration is closed.
                </p>
              </div>
              {error ? <p className="rs-error">{error}</p> : null}
              {editorSeed ? (
                <SunEditor
                  key={`${alertData?.id || "retreat"}-${editBox ? "edit" : "off"}`}
                  setContents={editorSeed}
                  onChange={(content) => {
                    setReason(content);
                    if (error) setError("");
                  }}
                  setOptions={EDITOR_OPTIONS}
                />
              ) : null}
            </section>
          ) : (
            <p className="rs-note">
              The inactive message stays saved, so you can turn registration off again later.
            </p>
          )}
        </div>

        <footer className="rs-footer">
          <button type="button" className="rs-btn rs-btn-quiet" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className={`rs-btn ${isActive && !editBox ? "rs-btn-warn" : "rs-btn-solid"}`}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </button>
        </footer>
      </div>
    </Modal>
  );
}

export default RetreatStatusModal;
